import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, XCircle, Loader2, GraduationCap, Clock, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

// Función para formatear RUT chileno
const formatRut = (value) => {
  let rut = value.replace(/[^0-9kK]/g, '').toUpperCase();
  if (rut.length === 0) return '';
  let dv = rut.slice(-1);
  let cuerpo = rut.slice(0, -1);
  let cuerpoFormateado = '';
  let contador = 0;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    cuerpoFormateado = cuerpo[i] + cuerpoFormateado;
    contador++;
    if (contador === 3 && i !== 0) {
      cuerpoFormateado = '.' + cuerpoFormateado;
      contador = 0;
    }
  }
  if (cuerpo.length === 0) return dv;
  return cuerpoFormateado + '-' + dv;
};

// Validar RUT Chileno
const validarRut = (rut) => {
  if (!rut) return false;
  const rutLimpio = rut.replace(/[^0-9kK]/g, '').toUpperCase();
  if (rutLimpio.length < 8) return false;
  const cuerpo = rutLimpio.slice(0, -1);
  const dvIngresado = rutLimpio.slice(-1);
  let suma = 0;
  let multiplicador = 2;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i]) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }
  const resto = suma % 11;
  const dvCalculado = resto === 0 ? '0' : resto === 1 ? 'K' : String(11 - resto);
  return dvIngresado === dvCalculado;
};

export default function InscripcionAcademiaPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [info, setInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    fecha_nacimiento: '',
    documento_tipo: 'rut',
    documento_numero: '',
    email: '',
    telefono: '',
    es_su_propio_apoderado: false,
    apoderado: {
      nombre: '',
      apellido: '',
      telefono: '',
      email: '',
      parentesco: 'padre'
    }
  });

  useEffect(() => {
    loadInfo();
  }, [token]);

  const loadInfo = async () => {
    try {
      const res = await axios.get(`${API}/academia/inscripcion/${token}`);
      setInfo(res.data);
    } catch (err) {
      setError('Este link de inscripción no es válido o ha sido desactivado.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim() || !formData.apellido.trim()) {
      toast.error('Por favor completa nombre y apellido');
      return;
    }
    
    if (!formData.documento_numero.trim()) {
      toast.error('Por favor ingresa el documento de identidad');
      return;
    }
    
    // Validar RUT si es Chile
    if (info?.pais === 'chile' && !validarRut(formData.documento_numero)) {
      toast.error('El RUT ingresado no es válido');
      return;
    }
    
    // Si no es su propio apoderado, validar datos del apoderado
    if (!formData.es_su_propio_apoderado) {
      if (!formData.apoderado.nombre.trim() || !formData.apoderado.telefono.trim()) {
        toast.error('Por favor completa los datos del apoderado');
        return;
      }
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/academia/inscripcion/${token}`, formData);
      setSuccess(true);
      toast.success('¡Solicitud enviada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900/30 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300">Cargando información...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-900/20 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Link no válido</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="border-slate-600 text-slate-300">
            Ir al inicio
          </Button>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900/20 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center max-w-md">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">¡Solicitud Enviada!</h2>
          <p className="text-slate-400 mb-6">
            Tu solicitud de inscripción ha sido enviada correctamente. 
            El administrador de la academia la revisará y te contactará pronto.
          </p>
          <div className="flex flex-col gap-3">
            <Button onClick={() => navigate('/')} className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500">
              Conocer Kinosis
            </Button>
            <Button onClick={() => window.close()} variant="outline" className="w-full border-slate-600 text-slate-400 hover:text-slate-300">
              Cerrar
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900/30 to-slate-900 py-4 sm:py-8 px-3 sm:px-4">
      <div className="max-w-xl mx-auto">
        {/* Header de la Academia */}
        <Card className="p-4 sm:p-6 bg-slate-800 border-slate-700 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 text-center sm:text-left">
            {info?.academia?.logo ? (
              <img src={info.academia.logo} alt="Logo" className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover flex-shrink-0" />
            ) : (
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-emerald-600/30 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-400" />
              </div>
            )}
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-white">{info?.academia?.nombre}</h1>
              <p className="text-emerald-400 font-medium text-sm sm:text-base">{info?.categoria?.nombre}</p>
            </div>
          </div>
          
          {/* Horarios */}
          {info?.categoria?.horarios?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <p className="text-xs sm:text-sm text-slate-400 mb-2">Horarios de la categoría:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {info.categoria.horarios.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs sm:text-sm text-slate-300">
                    <Clock className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                    <span>{DIAS_SEMANA[h.dia]}</span>
                    <span className="text-slate-500">{h.hora_inicio} - {h.hora_fin}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Formulario */}
        <Card className="p-4 sm:p-6 bg-slate-800 border-slate-700">
          <h2 className="text-base sm:text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-400" />
            Formulario de Inscripción
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Datos del Alumno */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-sm">Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Juan"
                  className="bg-slate-700 border-slate-600 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Apellido *</Label>
                <Input
                  value={formData.apellido}
                  onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                  placeholder="Pérez"
                  className="bg-slate-700 border-slate-600 text-white"
                  required
                />
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300 text-sm">
                {info?.pais === 'chile' ? 'RUT' : 'Documento de Identidad'} *
              </Label>
              <Input
                value={formData.documento_numero}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  documento_numero: info?.pais === 'chile' ? formatRut(e.target.value) : e.target.value 
                })}
                placeholder={info?.pais === 'chile' ? '12.345.678-9' : 'Número de documento'}
                className="bg-slate-700 border-slate-600 text-white"
                required
              />
            </div>
            
            <div>
              <Label className="text-slate-300 text-sm">Fecha de Nacimiento</Label>
              <Input
                type="date"
                value={formData.fecha_nacimiento}
                onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-sm">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@ejemplo.com"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Teléfono</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  placeholder="+56 9 1234 5678"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>

            {/* Checkbox Apoderado */}
            <div className="flex items-start gap-2 py-2">
              <Checkbox
                id="es_apoderado"
                checked={formData.es_su_propio_apoderado}
                onCheckedChange={(checked) => setFormData({ ...formData, es_su_propio_apoderado: checked })}
                className="mt-0.5"
              />
              <label htmlFor="es_apoderado" className="text-xs sm:text-sm text-slate-300 cursor-pointer leading-tight">
                Soy mayor de edad y seré mi propio apoderado
              </label>
            </div>

            {/* Datos del Apoderado */}
            {!formData.es_su_propio_apoderado && (
              <div className="bg-slate-700/30 p-3 sm:p-4 rounded-lg space-y-3">
                <h3 className="text-sm font-medium text-slate-300">Datos del Apoderado</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400 text-xs">Nombre *</Label>
                    <Input
                      value={formData.apoderado.nombre}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        apoderado: { ...formData.apoderado, nombre: e.target.value } 
                      })}
                      placeholder="Nombre del apoderado"
                      className="bg-slate-700 border-slate-600 text-white text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Apellido</Label>
                    <Input
                      value={formData.apoderado.apellido}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        apoderado: { ...formData.apoderado, apellido: e.target.value } 
                      })}
                      placeholder="Apellido"
                      className="bg-slate-700 border-slate-600 text-white text-sm"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400 text-xs">Teléfono *</Label>
                    <Input
                      value={formData.apoderado.telefono}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        apoderado: { ...formData.apoderado, telefono: e.target.value } 
                      })}
                      placeholder="+56 9 1234 5678"
                      className="bg-slate-700 border-slate-600 text-white text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Parentesco</Label>
                    <Select
                      value={formData.apoderado.parentesco}
                      onValueChange={(v) => setFormData({ 
                        ...formData, 
                        apoderado: { ...formData.apoderado, parentesco: v } 
                      })}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">
                        <SelectItem value="padre" className="text-white">Padre</SelectItem>
                        <SelectItem value="madre" className="text-white">Madre</SelectItem>
                        <SelectItem value="tutor" className="text-white">Tutor</SelectItem>
                        <SelectItem value="otro" className="text-white">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-400 text-xs">Email del Apoderado</Label>
                  <Input
                    type="email"
                    value={formData.apoderado.email}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      apoderado: { ...formData.apoderado, email: e.target.value } 
                    })}
                    placeholder="email@ejemplo.com"
                    className="bg-slate-700 border-slate-600 text-white text-sm"
                  />
                </div>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700 mt-4"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar Solicitud de Inscripción'
              )}
            </Button>
          </form>
        </Card>
        
        <p className="text-center text-slate-500 text-xs mt-4 px-2">
          Al enviar este formulario, tu solicitud será revisada por el administrador de la academia.
        </p>
      </div>
    </div>
  );
}
